import {FC, useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@material-ui/core";
import React from "react";
import {AccessibilityImages} from "../../../../../../../../../../models/AccessibilityImages";
import {useSelector} from "../../../../../../../../../../redux/store";
import {AppStateType} from "../../../../../../../../../../redux/reducers";
import GridDnd from "./components/GridDnd";
import {useDispatch} from "react-redux";
import {
  deleteUnitImagesByProperty,
  updateUnitImageByProperty
} from "../../../../../../../../../../redux/actions/unitImagesActions";
import {AccessibilityUnit} from "../../../../../../../../../../models/AccessibilityUnits";
import {useDebounce} from "../../../../../../../../../../hooks/useDebounce";
import {useTranslation} from "react-i18next";
import {getTextByCurrentLanguage} from "../../../../../../../../../../utils/functions";

type PropsType = {
  selectUnit: AccessibilityUnit;
  onSetShowImageEditor: (state: boolean) => void,
  forceUpdateById: string | null,
  onSetForceUpdateById: (id: string | null) => void,
  onSetEditData: (data: AccessibilityImages) => void
}

const ImagesList: FC<PropsType> = ({
  selectUnit,
  onSetShowImageEditor,
  forceUpdateById,
  onSetForceUpdateById,
  onSetEditData,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.authReducer.user);
  const images = useSelector((state: AppStateType) => state.unitImagesReducer.unitImagesByPropertyId)!
  const [localImages, setLocalImages] = useState<AccessibilityImages[] | null>(null)

  // useEffect for that when we select new unit
  useEffect(() => {
    if (selectUnit && images) {
      const filteredImagesByUnit = images.filter((i) => i.accessibility_unit === selectUnit.id)
      if (filteredImagesByUnit.length > 0) {
        setLocalImages(filteredImagesByUnit.sort((a,b) => a.order - b.order))
      } else {
        setLocalImages([])
      }
    }
  }, [selectUnit]);

  const isOrderCorrect = (arr: AccessibilityImages[]): boolean => {
    // Create an array that sequentially calculates a sequence of numbers
    const expectedOrder = Array.from({ length: arr.length }, (_, i) => i);
    // Checking if the "order" field of each element matches the expected value
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].order !== expectedOrder[i]) {
        return false;
      }
    }
    return true;
  }

  const updateImages = useDebounce((filteredImagesByUnit: AccessibilityImages[]) => {
    if (!isOrderCorrect(filteredImagesByUnit)) {
      filteredImagesByUnit.forEach(async (image, index) => {
        const foundImageIndex = images.findIndex((i) => i.id === image.id)
        await dispatch(updateUnitImageByProperty(foundImageIndex, image.id, { order: index }, user!.token))
      })
    }
    if (isOrderCorrect(filteredImagesByUnit)) {
      setLocalImages(filteredImagesByUnit.sort((a, b) => a.order - b.order))
    }
  }, 300);

  // add new image/delete image
  useEffect(() => {
    if (selectUnit && images) {
      const filteredImagesByUnit = images.filter((i) => i.accessibility_unit === selectUnit.id)
      if (filteredImagesByUnit.length > 0) {
        const isSameUnit = selectUnit.id === filteredImagesByUnit[0].accessibility_unit
        if (isSameUnit) {
          const isAddNewImage = localImages && localImages.length < filteredImagesByUnit.length
          const isDeleteImage = localImages && localImages.length > filteredImagesByUnit.length
          if (isAddNewImage || isDeleteImage) {
            updateImages(filteredImagesByUnit.sort((a, b) => {
              const orderComparison = a.order - b.order;
              if (orderComparison === 0) {
                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateB - dateA ;
              }
              return orderComparison;
            }));
          }
        }
      }
    }
    if (forceUpdateById) {
      const filteredImagesByUnit = images.filter((i) => i.accessibility_unit === forceUpdateById)
      updateImages(filteredImagesByUnit.sort((a, b) => {
        const orderComparison = a.order - b.order;
        if (orderComparison === 0) {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA ;
        }
        return orderComparison;
      }));
      onSetForceUpdateById(null)
    }
  }, [images, forceUpdateById]);

  const differingOrders = (array1: AccessibilityImages[], array2: AccessibilityImages[]) => {
    const arrayMap = new Map(array1.map((obj) => [obj.id, obj]));
    return array2.filter((obj2) => {
      const obj1 = arrayMap.get(obj2.id);
      return obj1 && obj1.order !== obj2.order;
    });
  }

  const differingGeneralImages = (array1: AccessibilityImages[], array2: AccessibilityImages[]) => {
    const arrayMap = new Map(array1.map((obj) => [obj.id, obj]));
    return array2.filter((obj2) => {
      const obj1 = arrayMap.get(obj2.id);
      return obj1 && obj1.is_general_image !== obj2.is_general_image;
    });
  }

  const handleUpdateImages = (updatedImages: AccessibilityImages[]) => {
    if (localImages) {
      const differentOrders = differingOrders(localImages, updatedImages)
      const differentGeneralImages = differingGeneralImages(localImages, updatedImages)
      if (differentOrders.length > 0) {
        for (const image of differentOrders) {
          const foundImageIndex = images.findIndex((i) => i.id === image.id)
          dispatch(updateUnitImageByProperty(
            foundImageIndex,
            image.id,
            {order: image.order, is_general_image: image.is_general_image},
            user!.token
          ))
        }
        setLocalImages(updatedImages)
        return
      }
      if (differentGeneralImages.length > 0) {
        differentGeneralImages.forEach( async (image) => {
          const foundImageIndex = images.findIndex((i) => i.id === image.id)
          dispatch(updateUnitImageByProperty(
            foundImageIndex,
            image.id,
            {order: image.order, is_general_image: image.is_general_image},
            user!.token
          ))
        })
        setLocalImages(updatedImages)
        return
      }
    }
  }

  const handleCheckbox = (data: AccessibilityImages) => {
    if (localImages) {
      const findIndex = images.findIndex((i) => i.id === data.id)
      const findIndexLocalImages = localImages.findIndex((i) => i.id === data.id)
      const mappedLocalImages = localImages.map((i, index) => {
        if (findIndexLocalImages === index) {
          return {
            ...i,
            is_display_in_output_service: data.is_display_in_output_service
          }
        }
        return i
      })
      setLocalImages(mappedLocalImages)
      const updatingFields = {
        is_display_in_output_service: data.is_display_in_output_service
      }
      dispatch(updateUnitImageByProperty(findIndex, data.id, updatingFields, user!.token))
    }
  }

  const handleDeleteImage = async (data: AccessibilityImages) => {
    if (localImages) {
      const findIndex = images.findIndex((i) => i.id === data.id)
      await dispatch(deleteUnitImagesByProperty(data.id, user!.token, findIndex))
      onSetForceUpdateById(data.accessibility_unit)
    }
  }

  const handleImageEditor = (data: AccessibilityImages) => {
    onSetShowImageEditor(true)
    onSetEditData(data)
  }

  return (
    <React.Fragment>
      <Box mb={2} ml={2}>
        <Typography style={{fontSize: 24, fontWeight: 500}}>{getTextByCurrentLanguage(selectUnit.name)}</Typography>
      </Box>
      {!localImages ? (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={400}>
          <CircularProgress color="secondary" />
        </Box>
      ) : localImages?.length === 0 ? (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={400}>
          <Typography variant={'h3'}>{t('statsPage_noDataAvailable_topText')}</Typography>
        </Box>
      ) : (
        <GridDnd
          images={localImages}
          onUpdateImages={handleUpdateImages}
          onSetEditorMode={handleImageEditor}
          onSetCheckbox={handleCheckbox}
          onDeleteImage={handleDeleteImage}
        />
      )}
    </React.Fragment>
  )
}

export default ImagesList